import { API_BASE_URL } from '../data/constants';
import { readStorageJSON, removeStorageItem, writeStorageJSON } from './storage';
import { getNetworkErrorMessage, logClientError } from './errors';

const TOKEN_KEY = 'payrocky_auth_token';
const BACKEND_HEALTH_URL = `${API_BASE_URL.replace(/\/api\/?$/, '')}/health`;
let lastBackendWarmAt = 0;
const inflightRequests = new Map();

export function getStoredToken() {
  return readStorageJSON(TOKEN_KEY, '');
}

export function setStoredToken(token) {
  if (!token) {
    removeStorageItem(TOKEN_KEY);
    return;
  }
  writeStorageJSON(TOKEN_KEY, token);
}

export function clearStoredToken() {
  removeStorageItem(TOKEN_KEY);
}

export async function ensureBackendReady(timeoutMs = 8000) {
  const now = Date.now();
  if (now - lastBackendWarmAt < 30 * 1000) {
    return true;
  }

  const resolvedTimeoutMs = Math.max(1000, Number(timeoutMs) || 8000);
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), resolvedTimeoutMs) : null;

  try {
    await fetch(BACKEND_HEALTH_URL, {
      method: 'GET',
      cache: 'no-store',
      signal: controller?.signal,
    });
    lastBackendWarmAt = Date.now();
    return true;
  } catch (_) {
    return false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function apiRequest(path, options = {}) {
  const {
    method = 'GET',
    body,
    token,
    headers = {},
    timeoutMs = 15000,
    idempotencyKey = '',
  } = options;

  const resolvedToken = token ?? getStoredToken();
  const normalizedMethod = String(method || 'GET').toUpperCase();
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const requestKey = normalizedMethod === 'GET'
    ? JSON.stringify({
        method: normalizedMethod,
        path,
        token: resolvedToken ? 'authenticated' : 'public',
      })
    : '';

  const executeRequest = async () => {
    const resolvedTimeoutMs = Math.max(1000, Number(timeoutMs) || 15000);
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timeoutTriggered = false;
    const timeoutId = controller
      ? setTimeout(() => {
          timeoutTriggered = true;
          controller.abort();
        }, resolvedTimeoutMs)
      : null;
    let timeoutRejectId = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutRejectId = setTimeout(() => {
        reject(new Error('Request timeout'));
      }, resolvedTimeoutMs);
    });

    const clearTimeouts = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (timeoutRejectId) clearTimeout(timeoutRejectId);
    };

    let response;
    try {
      response = await Promise.race([
        fetch(`${API_BASE_URL}${path}`, {
          method: normalizedMethod,
          headers: {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...(resolvedToken ? { Authorization: `Bearer ${resolvedToken}` } : {}),
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
            ...headers,
          },
          body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
          signal: controller?.signal,
        }),
        timeoutPromise,
      ]);
    } catch (error) {
      clearTimeouts();
      const message = String(error?.message || '');
      const isAbortError = error?.name === 'AbortError'
        || message.includes('signal is aborted')
        || message.includes('The operation was aborted');
      if (!isAbortError && !timeoutTriggered && !message.includes('Request timeout')) {
        logClientError(`API ${normalizedMethod} ${path}`, error);
      }
      throw new Error(getNetworkErrorMessage());
    }
    clearTimeouts();

    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = json.error || json.message || json.details?.userMessage || 'Request failed';
      logClientError(`API ${normalizedMethod} ${path}`, new Error(message), { status: response.status });
      const error = new Error(message);
      error.code = json.code || '';
      error.details = json.details || {};
      error.supportEmail = json.supportEmail || json.details?.supportEmail || '';
      throw error;
    }
    return json;
  };

  try {
    if (requestKey) {
      const existingRequest = inflightRequests.get(requestKey);
      if (existingRequest) {
        return await existingRequest;
      }

      const nextRequest = executeRequest().finally(() => {
        inflightRequests.delete(requestKey);
      });
      inflightRequests.set(requestKey, nextRequest);
      return await nextRequest;
    }

    return await executeRequest();
  } catch (error) {
    throw error;
  }
}
